// Frontend crash reporting (Phase 14.4). This audience (55–75, old Android /
// iOS Safari) is exactly where JS breaks, and backend Sentry can't see it.
//
// Sentry is a THIRD PARTY, so it's disclosed in the Privacy page and scoped as
// tightly as the app's privacy posture demands:
//   • no IP / no user identifiers      (sendDefaultPii: false)
//   • no tracing / no session replay   (errors only)
//   • query strings stripped from every URL — the located lookup carries the
//     user's GPS coords as ?lat=&lon=, which must never reach Sentry.
//
// Loaded via dynamic import ONLY when VITE_SENTRY_DSN is set, so the ~40 KB SDK
// never ships in the bundle when disabled (dev, tests, or until configured).

// Drop the query string (keeps the path, e.g. /api/products/<ean>) so GPS
// coordinates in ?lat=&lon= never leave the browser.
function stripQuery(url) {
  if (typeof url !== 'string') return url
  const q = url.indexOf('?')
  return q === -1 ? url : url.slice(0, q)
}

// The benign camera-teardown race: navigating away from the scanner unmounts the
// <video> while html5-qrcode's internal play() promise is still pending, so it
// rejects with AbortError ("The play() request was interrupted because the media
// was removed from the document"). The stream is still released — it's harmless
// (see https://goo.gl/LdLk22). Matched narrowly so real errors still surface.
export function isBenignMediaAbort(err) {
  return (
    err != null &&
    err.name === 'AbortError' &&
    typeof err.message === 'string' &&
    err.message.includes('play()')
  )
}

// Mark the benign play() AbortError as handled so it isn't an *unhandled*
// rejection (no console error, no report). Always installed, even when Sentry is
// off. Registered before Sentry so this listener runs first.
export function installBenignRejectionGuard() {
  window.addEventListener('unhandledrejection', (event) => {
    if (isBenignMediaAbort(event.reason)) event.preventDefault()
  })
}

export function initErrorMonitoring() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return

  import('@sentry/browser')
    .then((Sentry) => {
      Sentry.init({
        dsn,
        // Both piku apps share one DSN, so tag the source to separate real prod
        // crashes from the test host during the launch watch. Prefer an explicit
        // env var; otherwise derive from the host (canonical = production).
        environment:
          import.meta.env.VITE_SENTRY_ENVIRONMENT ||
          (window.location.hostname === 'provjericijene.hr' ? 'production' : 'staging'),
        sendDefaultPii: false, // no IP address, no user identifiers
        tracesSampleRate: 0, // errors only — no performance tracing
        // Drop crashes originating in third-party scripts we don't control. The
        // Cloudflare Web Analytics beacon calls Array.prototype.at(), which our
        // audience's old browsers (Safari <15.4, old Android) lack, so it throws
        // "t.entries.at is not a function" — harmless to us, pure Sentry noise.
        denyUrls: [/cloudflareinsights\.com/, /beacon\.min\.js/],
        // Scrub coords out of fetch/xhr/navigation breadcrumb URLs.
        beforeBreadcrumb(breadcrumb) {
          if (breadcrumb?.data?.url) breadcrumb.data.url = stripQuery(breadcrumb.data.url)
          if (breadcrumb?.data?.to) breadcrumb.data.to = stripQuery(breadcrumb.data.to)
          if (breadcrumb?.data?.from) breadcrumb.data.from = stripQuery(breadcrumb.data.from)
          return breadcrumb
        },
        // Scrub the request URL + drop any headers/cookies/query from the event.
        beforeSend(event, hint) {
          // Drop the benign camera-teardown AbortError (see isBenignMediaAbort):
          // Sentry's own unhandledrejection handler captures it regardless of the
          // guard's preventDefault, so filter it here too.
          if (isBenignMediaAbort(hint?.originalException)) return null
          if (event.request) {
            if (event.request.url) event.request.url = stripQuery(event.request.url)
            delete event.request.query_string
            delete event.request.cookies
            delete event.request.headers
          }
          return event
        },
      })
    })
    .catch(() => {
      // SDK failed to load — measurement is best-effort, never break the app.
    })
}
